import { useCallback } from 'react';
import { createNewListing } from '../models';
import { useFacility, useListing } from '../contexts/GroupIntakeRootContext';

/**
 * Hook for listing-related actions in the group intake form
 */
export const useListingActions = () => {
  const { 
    facilities, 
    setFacilities, 
    setShowDelListModal, 
    listToRemove, 
    setListToRemove,
    previewFacilityIndex,
    previewListingIndex,
    setPreviewListingIndex
  } = useFacility();
  const { updateListingField: contextUpdateListingField } = useListing();

  // Fallback for validateStep to avoid runtime error
  const validateStep = () => {};

  // Update a listing field
  // Use context's updateListingField for backend-persisted fields (like reviewStatus),
  // fallback to local update for others (legacy compatibility)
  const updateListingField = useCallback((facilityIndex, listingIndex, field, value) => {
    // If the field is reviewStatus, always use context (backend) update
    if (field === 'reviewStatus') {
      contextUpdateListingField(facilityIndex, listingIndex, field, value);
      return;
    }
    // Otherwise, update local state as before
    setFacilities(prev => {
      const updated = [...prev];
      const updatedListings = [...updated[facilityIndex].listings];
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedListings[listingIndex] = {
          ...updatedListings[listingIndex],
          [parent]: {
            ...(updatedListings[listingIndex][parent] || {}),
            [child]: value
          }
        };
      } else {
        updatedListings[listingIndex] = {
          ...updatedListings[listingIndex],
          [field]: value
        };
      }
      updated[facilityIndex].listings = updatedListings;
      return updated;
    });
    validateStep(2);
  }, [setFacilities, validateStep, contextUpdateListingField]);

  // Shorthand for updating clinical requirements
  const updateClinicalField = useCallback((facilityIndex, listingIndex, field, value) => {
    updateListingField(facilityIndex, listingIndex, `clinicalRequirements.${field}`, value);
  }, [updateListingField]);

  // Add a new listing to a facility
  const addListing = useCallback((facilityIndex) => {
    setFacilities(prev =>
      prev.map((f, i) =>
        i === facilityIndex 
          ? { ...f, listings: [...(f.listings || []), createNewListing()] } 
          : f
      )
    );
    validateStep(2);
  }, [setFacilities, validateStep]);

  // Request to remove a listing (show confirmation modal)
  const confirmRemoveListing = useCallback((facilityIndex, listingIndex) => {
    setListToRemove({ facIdx: facilityIndex, listIdx: listingIndex });
    setShowDelListModal(true);
  }, [setListToRemove, setShowDelListModal]);

  // Actually remove the listing after confirmation
  const removeListing = useCallback(() => {
    const { facIdx, listIdx } = listToRemove;
    
    setFacilities(prev =>
      prev.map((f, i) =>
        i !== facIdx
          ? f
          : { ...f, listings: f.listings.filter((_, j) => j !== listIdx) }
      )
    );
    
    setShowDelListModal(false);
    setListToRemove({ facIdx: null, listIdx: null });
    
    // Update preview listing index if needed
    const facility = facilities[facIdx];
    if (facility && listIdx >= facility.listings.length - 1) {
      setPreviewListingIndex(Math.max(0, facility.listings.length - 2));
    }
    
    validateStep(2);
  }, [
    facilities, 
    listToRemove, 
    setFacilities, 
    setListToRemove, 
    setPreviewListingIndex, 
    setShowDelListModal,
    validateStep
  ]);

  // Toggle publish/unpublish for a listing
  const togglePublish = useCallback((facilityIndex, listingIndex, currentStatus) => {
    const newStatus = currentStatus === 'reviewed' ? 'pending' : 'reviewed';
    // Always use context's updateListingField for reviewStatus to persist to backend
    contextUpdateListingField(facilityIndex, listingIndex, 'reviewStatus', newStatus);
  }, [contextUpdateListingField]);

  return {
    updateListingField,
    updateClinicalField,
    addListing,
    confirmRemoveListing,
    removeListing,
    togglePublish
  };
};

export default useListingActions;
